import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { formatDuration } from '../../utils/time';
import { SleepStageBar } from './SleepStageBar';
import { SleepGraph } from './SleepGraph';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SleepBreakdownProps {
  sleepStages: {
    rem: number;
    deep: number;
    light: number;
    awake: number;
  };
}

type SleepStage = 'awake' | 'light' | 'deep' | 'rem' | 'all';

export function SleepBreakdown({ sleepStages }: SleepBreakdownProps) {
  const [selectedStage, setSelectedStage] = useState<SleepStage>('all');
  const totalDuration = sleepStages.rem + sleepStages.deep + sleepStages.light + sleepStages.awake;
  const maxDuration = Math.max(sleepStages.rem, sleepStages.deep, sleepStages.light, sleepStages.awake);

  // Generate mock heart rate data with sleep stages
  const generateSleepData = () => {
    const data: { heartRate: number; stage: SleepStage }[] = [];
    const totalPoints = 480; // 8 hours in minutes
    let currentPoint = 0;

    // Helper to add data points for a stage
    const addStageData = (stage: SleepStage, duration: number, baseRate: number, variance: number) => {
      const points = Math.floor((duration / totalDuration) * totalPoints);
      for (let i = 0; i < points && currentPoint < totalPoints; i++) {
        const time = currentPoint / totalPoints;
        const cycle = Math.sin(time * Math.PI * 6) * variance;
        const noise = Math.random() * 3;
        data.push({
          heartRate: baseRate + cycle + noise,
          stage
        });
        currentPoint++;
      }
    };

    // Add data for each stage in sequence
    addStageData('light', sleepStages.light, 65, 5);
    addStageData('deep', sleepStages.deep, 55, 3);
    addStageData('rem', sleepStages.rem, 70, 8);
    addStageData('awake', sleepStages.awake, 75, 10);

    return data;
  };

  const sleepData = generateSleepData();
  const timeLabels = Array.from({ length: 12 }, (_, i) => {
    const hour = (22 + i) % 24;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  // Filter data based on selected stage
  const filteredData = selectedStage === 'all' 
    ? sleepData.map(d => d.heartRate)
    : sleepData
        .map(d => d.stage === selectedStage ? d.heartRate : null)
        .filter((hr): hr is number => hr !== null);

  return (
    <div className="theme-card p-6 space-y-6">
      <h3 className="text-lg font-semibold">Sleep Stages</h3>

      {/* Heart Rate Graph */}
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">Heart Rate</div>
        <SleepGraph 
          data={filteredData} 
          timeLabels={timeLabels} 
        />
      </div>

      {/* Sleep Stages */}
      <div className="space-y-4">
        <SleepStageBar
          stage="awake"
          duration={sleepStages.awake}
          percentage={(sleepStages.awake / totalDuration) * 100}
          maxDuration={formatDuration(maxDuration)}
          isSelected={selectedStage === 'awake'}
          onSelect={() => setSelectedStage(selectedStage === 'awake' ? 'all' : 'awake')}
        />
        <SleepStageBar
          stage="light"
          duration={sleepStages.light}
          percentage={(sleepStages.light / totalDuration) * 100}
          maxDuration={formatDuration(maxDuration)}
          isSelected={selectedStage === 'light'}
          onSelect={() => setSelectedStage(selectedStage === 'light' ? 'all' : 'light')}
        />
        <SleepStageBar
          stage="deep"
          duration={sleepStages.deep}
          percentage={(sleepStages.deep / totalDuration) * 100}
          maxDuration={formatDuration(maxDuration)}
          isSelected={selectedStage === 'deep'}
          onSelect={() => setSelectedStage(selectedStage === 'deep' ? 'all' : 'deep')}
        />
        <SleepStageBar
          stage="rem"
          duration={sleepStages.rem}
          percentage={(sleepStages.rem / totalDuration) * 100}
          maxDuration={formatDuration(maxDuration)}
          isSelected={selectedStage === 'rem'}
          onSelect={() => setSelectedStage(selectedStage === 'rem' ? 'all' : 'rem')}
        />
      </div>

      {/* Sleep Stage Distribution */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border">
        <div>
          <div className="text-sm text-muted-foreground">Deep Sleep</div>
          <div className="text-xl font-bold">{formatDuration(sleepStages.deep)}</div>
          <div className="text-xs text-success">
            {Math.round((sleepStages.deep / totalDuration) * 100)}% of total
          </div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">REM Sleep</div>
          <div className="text-xl font-bold">{formatDuration(sleepStages.rem)}</div>
          <div className="text-xs text-success">
            {Math.round((sleepStages.rem / totalDuration) * 100)}% of total
          </div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Light Sleep</div>
          <div className="text-xl font-bold">{formatDuration(sleepStages.light)}</div>
          <div className="text-xs text-muted-foreground">
            {Math.round((sleepStages.light / totalDuration) * 100)}% of total
          </div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Time Awake</div>
          <div className="text-xl font-bold">{formatDuration(sleepStages.awake)}</div>
          <div className="text-xs text-muted-foreground">
            {Math.round((sleepStages.awake / totalDuration) * 100)}% of total
          </div>
        </div>
      </div>
    </div>
  );
}